<template>
    <div
        ref="bannerRef"
        class="banner-preview"
        :class="{ 'banner-preview--click-disabled': !contentClickable }"
        :style="{
            width: metaPreviewSize?.width ? `${metaPreviewSize.width}px` : undefined,
            height: metaPreviewSize?.height ? `${metaPreviewSize.height}px` : undefined,
            maxHeight: metaPreviewSize?.maxHeight ? `${metaPreviewSize.maxHeight}px` : undefined,
            minHeight: metaPreviewSize?.minHeight ? `${metaPreviewSize.minHeight}px` : undefined
        }"
    >
        <div v-if="!isVisible">
            <slot name="placeholder" />
        </div>

        <template v-else>
            <div v-if="isLoading" class="banner__loading-spinner-wrapper">
                <hox-loading-spinner :size="40" />
            </div>

            <div v-else-if="isError" class="banner__error">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="errorMessage" />
            </div>

            <AEBanner
                v-if="isAE"
                :id="id"
                ref="banner"
                :preview-values="previewValues"
                :editor-values="editorValues"
                :playback-namespace="playbackNamespace"
                @loading="onLoading"
                @bannerError="onBannerError"
                @bannerLoaded="onBannerLoaded"
            />

            <PSDBanner
                v-else-if="isPSD"
                :id="id"
                :preview-values="previewValues"
                @loading="onLoading"
                @bannerError="onBannerError"
            />

            <InDesignBanner
                v-else-if="isInDesign"
                :id="id"
                :preview-values="previewValues"
                @loading="onLoading"
                @bannerError="onBannerError"
                @bannerLoaded="onBannerLoaded"
            />

            <HTML5Banner
                v-else
                :id="id"
                ref="banner"
                :debug="debug"
                :scaling-factor="scalingFactor"
                :preview-values="previewValues"
                :playback-namespace="playbackNamespace"
                @loading="onLoading"
                @bannerLoaded="onBannerLoaded"
            />
        </template>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, toRef, type PropType } from "vue";
import { useIntersectionObserver } from "@vueuse/core";
import { TemplateType } from "shared-utils/enums/masterTemplate";

import { BannerEvents } from "@/enums/banners";

import AEBanner from "@/components/Previews/AEBanner.vue";
import HTML5Banner from "@/components/Previews/HTML5Banner.vue";
import PSDBanner from "@/components/Previews/PSDBanner.vue";
import InDesignBanner from "@/components/Previews/InDesignBanner.vue";

export default defineComponent({
    name: "BannerPreview",
    components: {
        AEBanner,
        PSDBanner,
        HTML5Banner,
        InDesignBanner
    },
    props: {
        previewValues: {
            type: String,
            required: true
        },

        editorValues: {
            type: Object,
            default: () => ({})
        },

        id: {
            type: String,
            required: true
        },

        contentClickable: {
            type: Boolean,
            default: false
        },

        lazyLoad: {
            type: Boolean,
            default: false
        },

        debug: {
            type: Boolean,
            default: false
        },

        adType: {
            type: String,
            required: true
        },

        playbackNamespace: {
            type: String,
            required: true
        },

        scalingFactor: {
            type: Number,
            default: 1
        },

        metaPreviewSize: {
            type: Object as PropType<{ width?: number; height?: number; maxHeight?: number; minHeight?: number }>,
            required: false
        }
    },

    emits: [BannerEvents.BannerLoaded],

    setup(props) {
        const lazyLoad = toRef(props, "lazyLoad");

        const bannerRef = ref(null);
        const isVisible = ref(!lazyLoad.value);

        const { stop } = useIntersectionObserver(bannerRef, ([entry]) => {
            if (entry?.isIntersecting) {
                isVisible.value = true;
                stop();
            }
        });

        return {
            bannerRef,
            isVisible
        };
    },

    data() {
        return {
            isError: false,
            errorMessage: "",
            isLoading: true
        };
    },

    computed: {
        isPSD() {
            return this.adType === TemplateType.PSD;
        },

        isAE() {
            return this.adType === TemplateType.AE;
        },

        isInDesign() {
            return this.adType === TemplateType.INDESIGN;
        }
    },

    methods: {
        onLoading(isLoading: boolean) {
            this.isLoading = isLoading;

            if (isLoading) {
                this.isError = false;
                this.errorMessage = "";
            }
        },

        onBannerLoaded() {
            this.$emit(BannerEvents.BannerLoaded);
        },

        onBannerError(message = "There was an error loading the banner") {
            this.isLoading = false;
            this.isError = true;

            this.errorMessage = message;
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.banner__loading-spinner-wrapper {
    color: $cmp-light-font-color;
    position: absolute;
    right: 50%;
    top: 50%;
    transform: translate(50%, -50%);
}

.banner-preview {
    flex: 1 1 auto;
    // all this does is ensure no random whitespace is added to the bottom of the banner
    display: flex;

    background-color: $banner-preview-bg-color;
}

.banner-preview--click-disabled {
    pointer-events: none;
}

.banner__error {
    position: absolute;

    display: flex;
    justify-content: center;
    align-items: center;

    width: 100%;
    height: 100%;

    padding: 20px;
    background-color: #fff3cd;
}
</style>
